import { FC, useRef, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { Formik, FormikProps } from "formik"
import { object, string } from "yup"
import { RateTableConfigDetails } from "../../../types"
import ManageRateTableFormContent from "../../../common/ui/config-admin/config-rate-table/manage/form-content"
import Tabs from "../../../common/ui/tabs"
import ConfigEditHeader from "../../../common/ui/config-admin/config-edit-header"
import {
  useDeleteRateTableConfigMutation,
  useFetchAllBrandsQuery,
  useFetchDefaultRateTableConfigQuery,
  useFetchRateTableConfigDetailsQuery,
  useUpdateOrCreateRateTableConfigMutation
} from "../../../redux/queries"
import { ConfigEditPageSkeleton } from "../../../common/ui/skeletons"
import { useAppDispatch } from "../../../common/hooks"
import { useConfigMutationFeedback } from "../use-config-mutation-feedback"

const BACK_TAB = "/config-admin?tab=rate-table"
const backUrl = (brandId?: string) =>
  brandId ? `${BACK_TAB}&brand_id=${brandId}` : BACK_TAB

const TABS = [
  { id: "general", label: "General" },
  { id: "terms", label: "Terms" },
  { id: "presets", label: "Presets" },
  { id: "cta", label: "CTA" },
  { id: "advanced", label: "Advanced" }
]

const RateTableSchema = object({
  name: string().required().min(1),
  details: object().required(),
  brand_id: string().required().min(1)
})

const RateTableEditPage: FC = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()

  const isCreate = id === "new"
  const [tab, setTab] = useState(TABS[0].id)

  const { data: brands } = useFetchAllBrandsQuery({ limit: 999 })

  const { data: rateTableData, isLoading } =
    useFetchRateTableConfigDetailsQuery(
      { id: id ?? "" },
      { skip: isCreate || !id }
    )

  const { data: defaultData } = useFetchDefaultRateTableConfigQuery(
    {},
    { skip: !isCreate }
  )

  const [updateOrCreate, saveResult] =
    useUpdateOrCreateRateTableConfigMutation()
  const [deleteRateTable, deleteResult] = useDeleteRateTableConfigMutation()

  const formRef = useRef<FormikProps<RateTableConfigDetails>>(null)

  useConfigMutationFeedback({
    label: "rate table",
    isCreate,
    saveResult,
    deleteResult,
    navigate,
    backUrl: () => backUrl(formRef.current?.values.brand_id),
    dispatch
  })

  const raw = rateTableData ?? {
    name: "",
    id: "",
    brand_id: "",
    ...(defaultData || { is_default: "0", details: {} })
  }

  const initialValues: RateTableConfigDetails = {
    ...raw,
    details: raw.details ?? {}
  }

  const setFormField = (name: string, val: any) => {
    formRef.current?.setFieldValue(name, val)
  }

  const onSubmit = () => {
    if (formRef.current?.values) updateOrCreate(formRef.current.values)
  }

  const onDelete = () => {
    if (rateTableData?.id) deleteRateTable(rateTableData.id)
  }

  if (!isCreate && isLoading) {
    return <ConfigEditPageSkeleton tabCount={5} />
  }

  const pageTitle = isCreate
    ? "New rate table"
    : rateTableData?.name ?? "Rate table"

  return (
    <div className="min-h-screen bg-gray-50">
      <Formik
        initialValues={initialValues}
        onSubmit={onSubmit}
        innerRef={formRef}
        validationSchema={RateTableSchema}
        enableReinitialize
      >
        {({ values, isValid, dirty }) => (
          <>
            <ConfigEditHeader
              backLabel="Rate tables"
              namePlaceholder="Rate table name"
              name={values.name}
              pageTitle={pageTitle}
              isCreate={isCreate}
              isValid={isValid}
              dirty={dirty}
              isDefault={values.is_default === "1"}
              brands={brands ?? []}
              brandId={values.brand_id}
              onNameChange={val => setFormField("name", val)}
              onDefaultChange={v => setFormField("is_default", v ? "1" : "0")}
              onBrandChange={val => setFormField("brand_id", val)}
              onDelete={onDelete}
              onCancel={() => navigate(backUrl(values.brand_id))}
              onSubmit={onSubmit}
              onBack={() => navigate(backUrl(values.brand_id))}
            />

            <Tabs
              currentTab={tab}
              tabs={TABS}
              onChange={setTab}
              className="px-6 bg-white"
            />

            <div className="bg-white">
              <div className="max-w-2xl py-6 px-6">
                <ManageRateTableFormContent
                  values={values}
                  tab={tab}
                  setFormField={setFormField}
                />
              </div>
            </div>
          </>
        )}
      </Formik>
    </div>
  )
}

export default RateTableEditPage
